import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useInventory, VIEW_MODES, UNITS } from '../../contexts/InventoryContext';
import {
  readFiltersFromUrl, writeFiltersToUrl,
  readColumnsFromUrl, writeColumnsToUrl,
  readUnitFromUrl, writeUnitToUrl,
} from '../../contexts/inventoryFilterUrl';
import { DEFAULT_VISIBLE_COLUMNS } from '../../contexts/inventoryColumns';
import { loadUserViews } from '../../contexts/inventorySavedViews';
import useInventoryData, { scopeToUnit } from '../../hooks/useInventoryData';
import FileTree from '../FileTree.jsx';
import InventoryToolbar from './InventoryToolbar';
import UnitSwitch from './UnitSwitch';
import CheckTheseBanner from './CheckTheseBanner';
import SavedViewTabs from './SavedViewTabs';
import BulkActionBar from './BulkActionBar';
import InventoryTable from './InventoryTable';
import DocumentGallery from './DocumentGallery';
import DuplicateGroupPanel from './DuplicateGroupPanel';
import AgentPlanDrawer from '../agent_plans/AgentPlanDrawer.jsx';
import ClassificationDiff from '../agent_plans/renderers/ClassificationDiff.jsx';
import useHelpContextSignal from '../../hooks/useHelpContextSignal';
import { PREDEFINED_VIEWS } from '../../contexts/inventorySavedViews';

/**
 * DocumentInventory — main compositor for the document inventory view.
 *
 * Combines: unit switch (Files / Documents — plan §3.4c), folder sidebar,
 * toolbar, bulk actions, virtualized table or card grid, pager.
 *
 * This is a pure React replacement for the old iframe-based DocumentInventory.
 * All state comes from InventoryContext; data logic from useInventoryData.
 *
 * Props:
 *   caseId       — UUID
 *   documents    — array of document objects from AppContext
 *   folderTree   — tree data for FileTree sidebar
 *   onChatScope  — callback(docIds) to set chat analysis scope
 *   onShowDetail — callback(docId) to open detail modal
 *   onTreeChange — callback() when folder tree is mutated
 */
// How many rows each folder holds (ancestor-inclusive; a row counts once per folder it is in).
function countFolders(docs) {
  const byPath = new Map();
  let unfiled = 0;
  for (const d of docs) {
    const paths = (d.folder_paths && d.folder_paths.length) ? d.folder_paths : (d.folder_path ? [d.folder_path] : []);
    if (paths.length === 0) unfiled += 1;
    const seen = new Set();
    for (const path of paths) {
      const parts = String(path).split('/');
      for (let i = 1; i <= parts.length; i += 1) {
        const anc = parts.slice(0, i).join('/');
        if (seen.has(anc)) continue;
        seen.add(anc);
        byPath.set(anc, (byPath.get(anc) || 0) + 1);
      }
    }
  }
  return { total: docs.length, unfiled, byPath };
}

export default function DocumentInventory({
  caseId,
  documents = [],
  folderTree,
  lastPropagationRunAt = null,
  onChatScope,
  onShowDetail,
  onShowViewer,
  onTreeChange,
  onPropagated,
  checklistDocMap = {},
  bulkActionContext = null,
  inMode = false,
  onUseAsMatch = null,
  matchBusy = false,
}) {
  const { dispatch, ...state } = useInventory();
  const [showPropagate, setShowPropagate] = useState(false);

  // Help-context signal: how many docs are selected. Count only — no IDs.
  useHelpContextSignal('selected_document_count', state.selected?.size || 0);

  // URL ↔ filters[] persistence — hydrate on mount (case change), write on every change.
  // Order: URL chips win; if none, fall back to last-used saved view; if none, empty.
  // Skip the initial hydrate's URL write (would clobber the just-read state).
  const hydratedRef = useRef(false);
  useEffect(() => {
    hydratedRef.current = false;
    const fromUrl = readFiltersFromUrl();
    if (fromUrl.length > 0) {
      dispatch({ type: 'SET_FILTERS', filters: fromUrl });
    } else if (inMode) {
      // In an Inventory Mode (e.g. matchChecklistItem), the user opted
      // into a clean browse of the inventory. Skip the last-saved-view
      // fallback so they aren't trapped in a filtered subset.
      dispatch({ type: 'SET_FILTERS', filters: [] });
    } else {
      const { lastViewName } = loadUserViews(caseId);
      if (lastViewName) {
        const userViews = loadUserViews(caseId).views;
        const all = [...PREDEFINED_VIEWS, ...userViews];
        const view = all.find(v => v.name === lastViewName);
        if (view && view.filters.length > 0) {
          dispatch({ type: 'SET_FILTERS', filters: view.filters });
        }
        if (view && Array.isArray(view.columns) && view.columns.length > 0) {
          dispatch({ type: 'SET_COLUMNS', ids: view.columns });
        }
        if (view && view.unit) {
          dispatch({ type: 'SET_UNIT', unit: view.unit });
        }
      }
    }
    // ?cols= wins over saved-view columns (deep links are explicit user intent).
    const colsFromUrl = readColumnsFromUrl();
    if (colsFromUrl) {
      dispatch({ type: 'SET_COLUMNS', ids: colsFromUrl });
    }
    // ?unit= wins over everything — an Ask Arc link lands in the unit it names.
    const unitFromUrl = readUnitFromUrl();
    if (unitFromUrl) {
      dispatch({ type: 'SET_UNIT', unit: unitFromUrl });
    }
    hydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    writeFiltersToUrl(state.filters || []);
  }, [state.filters]);

  // Mirror visibleColumns into ?cols= — omit when the set matches the
  // registry default so shared URLs stay clean.
  useEffect(() => {
    if (!hydratedRef.current) return;
    const cols = state.visibleColumns || [];
    const isDefault = cols.length === DEFAULT_VISIBLE_COLUMNS.length
      && cols.every((id, i) => id === DEFAULT_VISIBLE_COLUMNS[i]);
    writeColumnsToUrl(cols, { omit: isDefault });
  }, [state.visibleColumns]);

  // Mirror the unit into ?unit= once the user has chosen one (automatic = no param).
  useEffect(() => {
    if (!hydratedRef.current) return;
    writeUnitToUrl(state.unit);
  }, [state.unit]);

  // The unit on screen (plan §3.4c). Automatic until the user picks: the documents
  // inside the files once any file has been cut, the files as uploaded otherwise.
  const hasCutFiles = useMemo(() => documents.some(d => d.is_compilation_container), [documents]);
  const unit = state.unit || (hasCutFiles ? UNITS.DOCUMENTS : UNITS.FILES);

  // Compute filtered/sorted/paged data — scoped to the unit first
  const data = useInventoryData(documents, { ...state, unit }, { checklistDocMap, lastPropagationRunAt });
  // List pages; Grouped and Grid show the whole sorted set (groups need every row)
  const visibleDocs = state.viewMode === VIEW_MODES.LIST ? data.pageSlice : data.sorted;

  // Folder counts follow the unit too: a folder that holds one cut file shows
  // "1" in Files and "22" in Documents. Ancestor-inclusive, like the server's.
  // `elsewhere` = what the other unit holds per folder, so the tree can leave out a
  // folder that is only empty in THIS unit (its documents were cut out of files);
  // `documents` = the Documents unit's counts — what Reorganize files (plan §7e);
  // what it never moves, per folder: `stays` = files cut into documents (already in a
  // folder), `handPlaced` = documents a person filed there, `copies` = second appearances
  // (a plan only changes a document's home).
  const folderCounts = useMemo(() => {
    const here = countFolders(data.unitDocs || []);
    const docs = countFolders(scopeToUnit(documents, UNITS.DOCUMENTS));
    const elsewhere = unit === UNITS.FILES ? docs.byPath : countFolders(scopeToUnit(documents, UNITS.FILES)).byPath;
    const home = (d) => ({ folder_path: d.folder_path });
    const stays = countFolders(documents.filter(d => d.is_compilation_container && d.folder_path).map(home)).byPath;
    const handPlaced = countFolders(
      documents.filter(d => !d.is_compilation_container && d.folder_path && d.folder_placed_by === 'person').map(home),
    ).byPath;
    const copies = countFolders(
      documents
        .map(d => ({ folder_paths: (d.folder_paths || []).filter(p => p !== d.folder_path) }))
        .filter(d => d.folder_paths.length > 0),
    ).byPath;
    // "Check these": the documents nobody has placed and the folders' rules could not place either
    const check = (data.unitDocs || []).filter(d => d.folder_check).length;
    return { ...here, unit, elsewhere, documents: docs.byPath, stays, handPlaced, copies, check };
  }, [data.unitDocs, documents, unit]);

  // Dim documents that are already matched to a checklist item OTHER
  // than the one we're currently picking for. Active only while a
  // matchChecklistItem mode is engaged. checklistDocMap shape:
  //   { <doc_id>: [<item_id>, ...] }
  const dimmedDocIds = useMemo(() => {
    if (bulkActionContext?.mode?.key !== 'matchChecklistItem') return null;
    const targetItemId = bulkActionContext?.modeContext?.itemId;
    if (!targetItemId) return null;
    const dimmed = new Set();
    for (const [docId, itemIds] of Object.entries(checklistDocMap || {})) {
      const list = Array.isArray(itemIds) ? itemIds : [];
      if (list.some((id) => id !== targetItemId)) dimmed.add(docId);
    }
    return dimmed;
  }, [bulkActionContext, checklistDocMap]);

  // # of selected docs that are still visible under the current filter.
  // Used by the bulk bar to render "N selected · M hidden by filter".
  const visibleSelectedCount = useMemo(() => {
    if (!state.selected || state.selected.size === 0) return 0;
    let n = 0;
    for (const d of data.filtered) if (state.selected.has(d.id)) n++;
    return n;
  }, [data.filtered, state.selected]);

  // Folder paths for move dropdowns
  const folderPaths = useMemo(() => {
    if (!folderTree?.tree) return [];
    const paths = [];
    function walk(nodes) {
      for (const n of nodes) {
        paths.push(n.path);
        if (n.children) walk(n.children);
      }
    }
    walk(folderTree.tree);
    return paths;
  }, [folderTree]);

  // Folder select handler
  const handleFolderSelect = useCallback((filterValue) => {
    dispatch({ type: 'SET_FOLDER', folder: filterValue });
  }, [dispatch]);

  // Doc mutation handler (refresh docs)
  const handleDocChange = useCallback(() => {
    onTreeChange?.();
  }, [onTreeChange]);

  // ── Resizable sidebar ──
  const SIDEBAR_MIN = 160;
  const SIDEBAR_MAX = 500;
  const SIDEBAR_DEFAULT = 220;

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('inv-sidebar-width');
    const w = saved ? Number(saved) : SIDEBAR_DEFAULT;
    return Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, w));
  });
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const onResizeStart = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = sidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [sidebarWidth]);

  useEffect(() => {
    function onMouseMove(e) {
      if (!dragging.current) return;
      const delta = e.clientX - startX.current;
      const w = Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, startW.current + delta));
      setSidebarWidth(w);
    }
    function onMouseUp() {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      localStorage.setItem('inv-sidebar-width', String(sidebarWidth));
    }
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [sidebarWidth]);

  return (
    <div className="inv-layout">
      {/* Sidebar */}
      <aside className="inv-sidebar" style={{ width: sidebarWidth }}>
        {folderTree && (
          <FileTree
            caseId={caseId}
            treeData={folderTree}
            counts={folderCounts}
            currentFilter={state.currentFolder}
            onSelect={handleFolderSelect}
            onTreeChange={onTreeChange}
          />
        )}
      </aside>

      {/* Resize handle */}
      <div className="inv-resize-handle" onMouseDown={onResizeStart} />

      {/* Main content. Add bottom padding when the bulk bar is open so
          the last row isn't covered by the fixed-position bar. */}
      <main className={`inv-main ${state.selected?.size > 0 ? 'inv-main--bulk-open' : ''}`}>
        <UnitSwitch
          unit={unit}
          counts={data.unitCounts}
          showSourceFiles={state.showSourceFiles}
          onChange={(u) => dispatch({ type: 'SET_UNIT', unit: u })}
          onToggleSources={(v) => dispatch({ type: 'SET_SHOW_SOURCE_FILES', value: v })}
        />
        {state.currentFolder === '__check__' && (
          <CheckTheseBanner
            caseId={caseId}
            docs={(data.unitDocs || []).filter(d => d.folder_check)}
            documents={documents}
            onFiled={onTreeChange}
          />
        )}
        <SavedViewTabs
          caseId={caseId}
          unit={unit}
          filters={state.filters || []}
          docsForFacets={data.docsForFacets}
          ctx={{ hasChecklist: Object.keys(checklistDocMap).length > 0, checklistDocMap, hasRedactedDocs: documents.some(d => d.is_redacted), hasDerivedDocs: documents.some(d => d.is_derived) }}
          onLoadView={(filters, columns, viewUnit) => {
            dispatch({ type: 'SET_FILTERS', filters });
            if (Array.isArray(columns) && columns.length > 0) {
              dispatch({ type: 'SET_COLUMNS', ids: columns });
            }
            if (viewUnit) dispatch({ type: 'SET_UNIT', unit: viewUnit });
          }}
        />
        <InventoryToolbar
          unit={unit}
          totalCount={data.filtered.length}
          visibleDocIds={visibleDocs.map(d => d.id)}
          filteredDocIds={data.sorted.map(d => d.id)}
          duplicateGroupCount={data.duplicateGroupCount}
          pendingCorrectionCount={data.pendingCorrectionCount}
          hasPendingPropagation={data.hasPendingPropagation}
          hasChecklist={Object.keys(checklistDocMap).length > 0}
          checklistDocMap={checklistDocMap}
          caseId={caseId}
          documents={documents}
          docsForFacets={data.docsForFacets}
          onDocChange={handleDocChange}
          onPropagate={data.hasPendingPropagation ? () => setShowPropagate(true) : undefined}
        />

        {/* The folder you clicked, said out loud. A folder that holds every document shows the same
            list as "All documents", so without this line a click looked like nothing happened
            (Denise, 2026-10-14). When the folder has nothing on this side but something on the
            other, say that and offer the switch. */}
        {state.currentFolder && !String(state.currentFolder).startsWith('__') && (() => {
          const path = String(state.currentFolder);
          const name = path.split('/').filter(Boolean).pop() || path;
          const noun = unit === UNITS.FILES ? 'file' : 'document';
          const here = data.filtered.length;
          const elsewhere = folderCounts?.elsewhere?.get(path) || 0;
          const otherUnit = unit === UNITS.FILES ? UNITS.DOCUMENTS : UNITS.FILES;
          const otherNoun = otherUnit === UNITS.FILES ? 'file' : 'document';
          return (
            <div className="inv-folder-line" data-testid="folder-line">
              <span className="inv-folder-line-icon" aria-hidden="true">📁</span>
              <span className="inv-folder-line-name">{name}</span>
              <span className="inv-folder-line-count">
                {here === 0 && elsewhere > 0
                  ? `nothing on the ${noun === 'file' ? 'Files' : 'Documents'} side — it holds ${elsewhere} ${otherNoun}${elsewhere === 1 ? '' : 's'}`
                  : `${here} ${noun}${here === 1 ? '' : 's'}`}
              </span>
              {here === 0 && elsewhere > 0 && (
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => dispatch({ type: 'SET_UNIT', unit: otherUnit })}>
                  Show {otherNoun === 'file' ? 'Files' : 'Documents'}
                </button>
              )}
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => dispatch({ type: 'SET_FOLDER', folder: '__all__' })} title="Back to every document">
                ✕ Show all
              </button>
            </div>
          );
        })()}

        <BulkActionBar
          caseId={caseId}
          documents={documents}
          folderPaths={folderPaths}
          onDocChange={handleDocChange}
          onChatScope={onChatScope}
          visibleSelectedCount={visibleSelectedCount}
          extraContext={bulkActionContext}
        />

        {(state.duplicateFilter || state.duplicateHashFilter) && (
          <>
            {state.duplicateHashFilter && (
              <div className="dup-hash-banner">
                <span>Showing duplicates of: <strong>{documents.find(d => d.file_hash_sha256 === state.duplicateHashFilter)?.filename || 'unknown'}</strong></span>
                <button className="btn btn-sm dup-hash-clear" onClick={() => dispatch({ type: 'CLEAR_DUPLICATE_HASH_FILTER' })}>✕ Clear</button>
              </div>
            )}
            <DuplicateGroupPanel
              documents={documents}
              hashCountMap={data.hashCountMap}
              caseId={caseId}
              onDocChange={handleDocChange}
              filterHash={state.duplicateHashFilter}
            />
          </>
        )}

        {state.viewMode === VIEW_MODES.GRID ? (
          <DocumentGallery
            caseId={caseId}
            unit={unit}
            docs={visibleDocs}
            allDocs={documents}
            folderPaths={folderPaths}
            hashCountMap={data.hashCountMap}
            onDocChange={handleDocChange}
            onShowDetail={onShowDetail}
            onShowViewer={onShowViewer}
          />
        ) : (
          <InventoryTable
            docs={visibleDocs}
            allDocs={documents}
            caseHasDocuments={documents.length > 0}
            typeGroups={data.typeGroups}
            caseId={caseId}
            folderPaths={folderPaths}
            onDocChange={handleDocChange}
            onShowDetail={onShowDetail}
            onShowViewer={onShowViewer}
            hashCountMap={data.hashCountMap}
            dimmedDocIds={dimmedDocIds}
            onUseAsMatch={onUseAsMatch}
            matchBusy={matchBusy}
          />
        )}

        {/* Pager — list mode (grouped and grid show every row under their headers) */}
        {state.viewMode === VIEW_MODES.LIST && data.totalPages > 1 && (
          <div className="inv-pager">
            <button
              className="btn btn-sm"
              disabled={state.page <= 1}
              onClick={() => dispatch({ type: 'SET_PAGE', page: state.page - 1 })}
            >
              ← Prev
            </button>
            <span className="inv-pager-info">
              Page {state.page} of {data.totalPages}
            </span>
            <button
              className="btn btn-sm"
              disabled={state.page >= data.totalPages}
              onClick={() => dispatch({ type: 'SET_PAGE', page: state.page + 1 })}
            >
              Next →
            </button>
            <select
              className="inv-select inv-select--pagesize"
              value={state.pageSize}
              onChange={e => dispatch({ type: 'SET_PAGE', page: 1, pageSize: Number(e.target.value) })}
            >
              {[25, 50, 100, 250].map(n => (
                <option key={n} value={n}>{n} per page</option>
              ))}
            </select>
          </div>
        )}
      </main>

      {showPropagate && (
        <AgentPlanDrawer
          caseId={caseId}
          surfaceId="drawer.propagate"
          PlanRenderer={ClassificationDiff}
          onApplied={() => { setShowPropagate(false); onTreeChange?.(); onPropagated?.(); }}
          onClose={() => setShowPropagate(false)}
          labels={{
            title: 'Propagate Corrections',
            runningMsg: 'Scanning for similar documents…',
            readyMsg: 'Review the proposed reclassifications below. Uncheck any you want to skip.',
            applyBtn: 'Apply reclassifications',
            doneMsg: '✓ Classifications updated.',
          }}
        />
      )}
    </div>
  );
}
